import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ToastContainer';
import ConnectModal from '../components/ConnectModal';
import * as api from '../services/api';
import {
  formatBytes,
  formatBytesFromGB,
  formatDuration,
  formatDurationFromHours,
} from '../utils/format';
import './Dashboard.css';

const truthyFlag = (value) => value === true || value === 'true' || value === 1 || value === '1';

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isCompanyAdmin } = useAuth();
  const { showError, showWarning } = useToast();

  const [overview, setOverview] = useState(null);
  const [groupedNodes, setGroupedNodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);

  const companyAdmin = useMemo(
    () =>
      truthyFlag(isCompanyAdmin) ||
      truthyFlag(user?.is_company_admin) ||
      truthyFlag(localStorage.getItem('isCompanyAdmin')),
    [isCompanyAdmin, user]
  );

  const loadDashboardData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [dash, nodesResponse] = await Promise.all([
        api.getDashboardOverview(),
        api.getAllNodesWithAccess(),
      ]);
      setOverview(dash || null);
      setGroupedNodes(nodesResponse?.grouped || {});
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => loadDashboardData({ silent: true }), 20000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  const stats = overview?.stats || {};
  const active = overview?.active_connection || null;
  const recent = overview?.recent_sessions || [];
  const plan = overview?.plan_type || '';

  const regions = useMemo(() => {
    const fromApi = overview?.regions || [];
    if (fromApi.length > 0) return fromApi;
    // Fallback if overview regions are empty but nodes payload exists
    return Object.keys(groupedNodes || {}).map((code) => {
      const countryData = groupedNodes[code] || {};
      const servers = [];
      if (countryData.locations) {
        Object.values(countryData.locations).forEach((list) => servers.push(...(list || [])));
      }
      const accessible = servers.filter((s) => s.has_access).length;
      const online = servers.filter((s) => s.status === 'online').length;
      const avgLoad =
        online > 0
          ? servers
              .filter((s) => s.status === 'online')
              .reduce((sum, s) => sum + (s.load_percent || 0), 0) / online
          : 0;
      const label =
        countryData.country && countryData.country !== 'Unknown'
          ? countryData.country
          : code && code !== 'XX'
            ? code
            : 'Unknown';
      return {
        country_code: code,
        country: label,
        flag_emoji: countryData.flag_emoji || '',
        server_count: servers.length,
        accessible_count: accessible,
        online_count: online,
        avg_load: avgLoad,
      };
    });
  }, [overview?.regions, groupedNodes]);

  const greeting = useMemo(() => {
    const hour = new Date().getHours(); // browser local time
    if (hour >= 5 && hour < 12) return t('dashboard.greeting.morning', 'Good morning');
    if (hour >= 12 && hour < 18) return t('dashboard.greeting.afternoon', 'Good afternoon');
    if (hour >= 18 && hour < 22) return t('dashboard.greeting.evening', 'Good evening');
    return t('dashboard.greeting.night', 'Good night');
  }, [t]);

  const countryServers = (countryCode) => {
    const countryData = groupedNodes[countryCode];
    const servers = [];
    if (countryData?.locations) {
      Object.values(countryData.locations).forEach((list) => servers.push(...list));
    }
    return servers;
  };

  const handleUpgradeNav = () => {
    if (companyAdmin) {
      navigate('/dashboard/upgrade');
      return;
    }
    showWarning(
      t(
        'dashboard.upgradeAskAdmin',
        'Someone with company management permission can upgrade the package.'
      )
    );
  };

  const handleServerClick = (server) => {
    if (!server.has_access) {
      handleUpgradeNav();
      return;
    }
    setSelectedNode(server);
    setShowConnectModal(true);
  };

  const handleDisconnect = async () => {
    if (!active?.id || disconnecting) return;
    setDisconnecting(true);
    try {
      await api.disconnectFromNode(active.id);
      await loadDashboardData({ silent: true });
    } catch (error) {
      console.error('Failed to disconnect:', error);
      showError(error.message || t('dashboard.disconnectFailed', 'Disconnect failed'));
    } finally {
      setDisconnecting(false);
    }
  };

  const formatWhen = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString(i18n.language || undefined, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !overview) {
    return (
      <div className="db-page">
        <div className="db-empty">
          <div className="db-spinner" />
          <p>{t('common.loading', 'Loading…')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="db-page">
      {user && !user.email_verified && (
        <div className="db-verify-overlay">
          <div className="db-verify-card">
            <p className="db-kicker">{t('dashboard.verification.kicker', 'Verify')}</p>
            <h2>{t('dashboard.verification.title', 'Confirm your email')}</h2>
            <p>{t('dashboard.verification.message', 'Verify your email to unlock secure connections.')}</p>
            <p className="db-verify-email">{user.email}</p>
            <button type="button" className="db-btn primary" onClick={() => navigate('/verify-email')}>
              {t('dashboard.verification.button', 'Verify now')}
            </button>
          </div>
        </div>
      )}

      <div className={`db-content${user && !user.email_verified ? ' db-blur' : ''}`}>
        <section className="db-hero">
          <div className="db-hero-copy">
            <p className="db-kicker">{t('dashboard.kicker', 'Operations')}</p>
            <h1>
              {greeting}, {user?.username || overview?.user?.username || 'there'}
            </h1>
            <p className="db-lead">
              {t(
                'dashboard.lead',
                'Your secure network at a glance — live tunnels, regions, and recent sessions.'
              )}
            </p>
            <p className="db-lead-mobile">
              {t('dashboard.leadMobile', 'Pick a region below to connect.')}
            </p>
            <div className="db-hero-actions">
              <button type="button" className="db-btn primary" onClick={() => navigate('/dashboard/connections')}>
                {t('dashboard.actions.sessions', 'View sessions')}
              </button>
              <button type="button" className="db-btn ghost" onClick={() => navigate('/dashboard/downloads')}>
                {t('dashboard.actions.clients', 'Get clients')}
              </button>
            </div>
          </div>

          <div className={`db-live ${active ? 'is-on' : ''}`}>
            <div className={`db-pulse ${active ? 'is-on' : ''}`} />
            <div>
              <strong>
                {active
                  ? t('dashboard.live.protected', 'Protected')
                  : t('dashboard.live.idle', 'Idle')}
              </strong>
              <span>
                {active
                  ? `${active.flag_emoji || ''} ${active.node_name || active.node_location}`
                  : t('dashboard.live.choose', 'Choose a region to connect')}
              </span>
            </div>
            {plan && <em className="db-plan">{String(plan).toUpperCase()}</em>}
          </div>
        </section>

        <section className="db-stats">
          <article className="db-stat">
            <span>{t('dashboard.stats.connections', 'Active')}</span>
            <strong>{stats.active_connections || 0}</strong>
          </article>
          <article className="db-stat">
            <span>{t('dashboard.stats.data', 'Data used')}</span>
            <strong>{formatBytesFromGB(stats.total_data_gb || 0)}</strong>
          </article>
          <article className="db-stat">
            <span>{t('dashboard.stats.connectionTime', 'Time online')}</span>
            <strong>{formatDurationFromHours(stats.total_time_hours)}</strong>
          </article>
          <article className="db-stat">
            <span>{t('dashboard.stats.today', 'Sessions today')}</span>
            <strong>{stats.connections_today || 0}</strong>
          </article>
          <article className="db-stat">
            <span>{t('dashboard.stats.servers', 'Servers')}</span>
            <strong>
              {stats.online_nodes || 0}/{stats.available_nodes || 0}
            </strong>
          </article>
        </section>

        {active && (
          <section className="db-tunnel">
            <div className="db-tunnel-main">
              <div className="db-signal" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </div>
              <div>
                <p className="db-kicker">{t('dashboard.tunnel.live', 'Live tunnel')}</p>
                <h2>
                  {active.flag_emoji} {active.node_name}
                </h2>
                <p>{active.node_location}</p>
              </div>
            </div>
            <div className="db-tunnel-meta">
              <div>
                <span>{t('dashboard.tunnel.duration', 'Duration')}</span>
                <strong>{formatDuration(active.duration_seconds)}</strong>
              </div>
              <div>
                <span>{t('dashboard.tunnel.transfer', 'Transfer')}</span>
                <strong>{formatBytes((active.bytes_sent || 0) + (active.bytes_received || 0))}</strong>
              </div>
              <div>
                <span>{t('dashboard.tunnel.started', 'Started')}</span>
                <strong>{formatWhen(active.connected_at)}</strong>
              </div>
            </div>
            <button
              type="button"
              className="db-btn danger"
              disabled={disconnecting}
              onClick={handleDisconnect}
            >
              {disconnecting
                ? t('dashboard.ending', 'Ending…')
                : t('dashboard.disconnect', 'End session')}
            </button>
          </section>
        )}

        <section className="db-quick">
          <button type="button" className="db-quick-card" onClick={() => navigate('/dashboard/notifications')}>
            <strong>{stats.unread_notifications || 0}</strong>
            <span>{t('dashboard.quick.notifications', 'Unread alerts')}</span>
          </button>
          <button type="button" className="db-quick-card" onClick={() => navigate('/dashboard/tickets')}>
            <strong>{stats.open_tickets || 0}</strong>
            <span>{t('dashboard.quick.tickets', 'Open tickets')}</span>
          </button>
          <button type="button" className="db-quick-card" onClick={() => navigate('/dashboard/connections')}>
            <strong>{recent.length}</strong>
            <span>{t('dashboard.quick.recent', 'Recent sessions')}</span>
          </button>
          <button type="button" className="db-quick-card" onClick={() => navigate('/dashboard/downloads')}>
            <strong>{t('dashboard.quick.get', 'Get')}</strong>
            <span>{t('dashboard.quick.clients', 'Client apps')}</span>
          </button>
        </section>

        <section className="db-workspace">
          <div className="db-panel">
            <header className="db-panel-head">
              <div>
                <p className="db-kicker">{t('dashboard.regions.kicker', 'Network')}</p>
                <h2>{t('dashboard.regions.title', 'Global regions')}</h2>
              </div>
              <span className="db-chip">{regions.length} {t('dashboard.regions.count', 'regions')}</span>
            </header>

            {regions.length === 0 ? (
              <div className="db-empty soft">
                <h3>{t('dashboard.regions.empty', 'No regions published yet')}</h3>
                <p>{t('dashboard.regions.emptyDesc', 'Approved servers will appear here.')}</p>
              </div>
            ) : (
              <div className="db-regions">
                {regions.map((region) => {
                  const code = region.country_code;
                  const servers = countryServers(code);
                  const open = selectedCountry === code;
                  const locked = (region.accessible_count || 0) === 0;
                  return (
                    <div key={code || region.country} className={`db-region ${open ? 'is-open' : ''} ${locked ? 'is-locked' : ''}`}>
                      <button
                        type="button"
                        className="db-region-btn"
                        onClick={() => setSelectedCountry(open ? null : code)}
                      >
                        <span className="db-flag">{region.flag_emoji || '◎'}</span>
                        <span className="db-region-copy">
                          <strong>
                            {region.country && region.country !== 'Unknown' && region.country !== 'XX'
                              ? region.country
                              : code && code !== 'XX'
                                ? code
                                : t('dashboard.regions.unknown', 'Unknown')}
                          </strong>
                          <em>
                            {region.online_count}/{region.server_count}{' '}
                            {t('dashboard.regions.online', 'online')}
                            {locked
                              ? ` · ${t('dashboard.regions.upgrade', 'Upgrade')}`
                              : ` · ${Math.round(region.avg_load || 0)}% ${t('dashboard.regions.load', 'load')}`}
                          </em>
                        </span>
                        <span className="db-chip">{open ? '−' : '+'}</span>
                      </button>
                      {open && (
                        <div className="db-servers">
                          {servers.length === 0 ? (
                            <p className="db-muted">{t('dashboard.regions.noServers', 'No servers in this region.')}</p>
                          ) : (
                            servers.map((server) => (
                              <button
                                key={server.id}
                                type="button"
                                className={`db-server ${server.has_access ? '' : 'locked'}`}
                                onClick={() => handleServerClick(server)}
                              >
                                <span>
                                  <strong>{server.name}</strong>
                                  <em>{server.location}</em>
                                </span>
                                {server.has_access ? (
                                  <span className="db-load">
                                    <span className="db-load-track">
                                      <i style={{ width: `${Math.min(100, server.load_percent || 0)}%` }} />
                                    </span>
                                    <em>{server.load_percent || 0}%</em>
                                  </span>
                                ) : (
                                  <span className="db-chip warn">{t('dashboard.upgrade', 'Upgrade')}</span>
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <aside className="db-panel db-side">
            <header className="db-panel-head">
              <div>
                <p className="db-kicker">{t('dashboard.recent.kicker', 'History')}</p>
                <h2>{t('dashboard.recent.title', 'Recent sessions')}</h2>
              </div>
              <button type="button" className="db-link" onClick={() => navigate('/dashboard/connections')}>
                {t('dashboard.recent.all', 'All')}
              </button>
            </header>

            {recent.length === 0 ? (
              <div className="db-empty soft">
                <h3>{t('dashboard.recent.empty', 'No sessions yet')}</h3>
                <p>{t('dashboard.recent.emptyDesc', 'Connect to a region to start your first tunnel.')}</p>
              </div>
            ) : (
              <ul className="db-recent">
                {recent.map((session) => (
                  <li key={session.id}>
                    <span className="db-flag sm">{session.flag_emoji || '◎'}</span>
                    <span>
                      <strong>{session.node_name || '—'}</strong>
                      <em>
                        {formatWhen(session.connected_at)} · {formatDuration(session.duration_seconds)}
                      </em>
                    </span>
                    <span className={`db-chip ${session.status === 'active' ? 'ok' : ''}`}>
                      {session.status === 'active'
                        ? t('dashboard.status.live', 'Live')
                        : t('dashboard.status.ended', 'Ended')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </section>
      </div>

      {showConnectModal && selectedNode && (
        <ConnectModal
          node={selectedNode}
          onClose={() => {
            setShowConnectModal(false);
            setSelectedNode(null);
          }}
          onSuccess={() => {
            loadDashboardData({ silent: true });
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
